"use client"

import React, { useState, useEffect, useRef } from "react"
import { motion, useScroll, useTransform, useSpring } from "framer-motion"
import { cn } from "@/lib/utils"
import { roadmapData } from "@/app/roadmap/roadmap-timeline/lib/roadmap-data"
import { TimelineItem } from "./components/timeline-item"

export interface TimelineEvent {
    id?: string
    year: string
    title: string
    subtitle?: string
    description: { text: string; completed: boolean }[]
    icon?: React.ReactNode
    color?: string
}

export interface ScrollTimelineProps {
    events: TimelineEvent[]
    title?: string
    subtitle?: string
    animationOrder?: "sequential" | "staggered" | "simultaneous"
    cardAlignment?: "alternating" | "left" | "right"
    lineColor?: string
    activeColor?: string
    progressIndicator?: boolean
    cardVariant?: "default" | "elevated" | "outlined" | "filled"
    cardEffect?: "none" | "glow" | "shadow" | "bounce"
    parallaxIntensity?: number
    progressLineWidth?: number
    progressLineCap?: "round" | "square"
    dateFormat?: "text" | "badge"
    className?: string
    revealAnimation?: "fade" | "slide" | "scale" | "flip" | "none"
    connectorStyle?: "dots" | "line" | "dashed"
    perspective?: boolean
    darkMode?: boolean
    smoothScroll?: boolean
}

export const ScrollTimeline = ({
    events = roadmapData,
    title = "Roadmap",
    subtitle = "Our strategic plan to dominate the meme coin universe.",
    animationOrder = "sequential",
    cardAlignment = "alternating",
    lineColor = "bg-primary/30",
    activeColor: _activeColor = "bg-primary",
    progressIndicator = true,
    cardVariant = "default",
    cardEffect = "none",
    parallaxIntensity = 0.2,
    progressLineWidth = 2,
    progressLineCap = "round",
    revealAnimation = "fade",
    className = "",
    connectorStyle = "line",
    perspective = false,
    darkMode = false,
    smoothScroll: _smoothScroll = true,
}: ScrollTimelineProps) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const [activeIndex, setActiveIndex] = useState(-1)
    const timelineRefs = useRef<(HTMLDivElement | null)[]>([])

    const { scrollYProgress } = useScroll({
        target: scrollRef,
        offset: ["start start", "end end"],
    })

    const smoothProgress = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001,
    })

    const progressHeight = useTransform(smoothProgress, [0, 1], ["0%", "100%"])

    useEffect(() => {
        const unsubscribe = scrollYProgress.onChange((v) => {
            const newIndex = Math.floor(v * events.length)
            if (
                newIndex !== activeIndex &&
                newIndex >= 0 &&
                newIndex < events.length
            ) {
                setActiveIndex(newIndex)
            }
        })
        return () => unsubscribe()
    }, [scrollYProgress, events.length, activeIndex])

    const getCardVariants = (index: number) => {
        const baseDelay =
            animationOrder === "simultaneous"
                ? 0
                : animationOrder === "staggered"
                  ? index * 0.2
                  : index * 0.3

        const initialStates = {
            fade: { opacity: 0, y: 20 },
            slide: {
                x:
                    cardAlignment === "left"
                        ? -100
                        : cardAlignment === "right"
                          ? 100
                          : index % 2 === 0
                            ? -100
                            : 100,
                opacity: 0,
            },
            scale: { scale: 0.8, opacity: 0 },
            flip: { rotateY: 90, opacity: 0 },
            none: { opacity: 1 },
        }

        return {
            initial: initialStates[revealAnimation],
            whileInView: {
                opacity: 1,
                y: 0,
                x: 0,
                scale: 1,
                rotateY: 0,
                transition: {
                    duration: 0.7,
                    delay: baseDelay,
                    ease: [0.25, 0.1, 0.25, 1.0] as [
                        number,
                        number,
                        number,
                        number,
                    ],
                },
            },
            viewport: { once: false, margin: "-100px" },
        }
    }

    const getConnectorClasses = () => {
        const baseClasses = cn(
            "absolute left-1/2 -translate-x-1/2 transform",
            lineColor,
        )
        const widthStyle = `w-[${progressLineWidth}px]`
        switch (connectorStyle) {
            case "dots":
                return cn(baseClasses, "w-1 rounded-full")
            case "dashed":
                return cn(
                    baseClasses,
                    widthStyle,
                    `[mask-image:linear-gradient(to_bottom,black_33%,transparent_33%,transparent_66%,black_66%)] [mask-size:1px_12px]`,
                )
            case "line":
            default:
                return cn(baseClasses, widthStyle)
        }
    }

    const getCardClasses = (index: number) => {
        const baseClasses = "relative z-30 rounded-lg "
        const variantClasses = {
            default: "bg-card border shadow-sm",
            elevated: "bg-card border border-border/40 shadow-md",
            outlined: "bg-card/50 backdrop-blur border-2 border-primary/20",
            filled: "bg-primary/10 border border-primary/30",
        }
        const effectClasses = {
            none: "",
            glow: "hover:shadow-[0_0_15px_rgba(var(--primary-rgb)/0.5)]",
            shadow: "hover:shadow-lg hover:-translate-y-1",
            bounce: "hover:scale-[1.03] hover:shadow-md active:scale-[0.97]",
        }
        const alignmentClassesDesktop =
            cardAlignment === "alternating"
                ? index % 2 === 0
                    ? "lg:mr-[calc(50%+20px)]"
                    : "lg:ml-[calc(50%+20px)]"
                : cardAlignment === "left"
                  ? "lg:mr-auto lg:ml-0"
                  : "lg:ml-auto lg:mr-0"
        const perspectiveClass = perspective
            ? "transform transition-transform hover:rotate-y-1 hover:rotate-x-1"
            : ""

        return cn(
            baseClasses,
            variantClasses[cardVariant],
            effectClasses[cardEffect],
            alignmentClassesDesktop,
            perspectiveClass,
            "w-full lg:w-[calc(50%-40px)]",
        )
    }

    return (
        <div
            ref={scrollRef}
            className={cn(
                "relative min-h-screen w-full overflow-hidden",
                darkMode ? "bg-background text-foreground" : "",
                className,
            )}
        >
            <div className="px-4 py-16 text-center">
                <h2 className="mb-4 text-3xl font-bold md:text-5xl">{title}</h2>
                <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
                    {subtitle}
                </p>
            </div>

            <div className="relative mx-auto max-w-6xl px-4 pb-24">
                <div className="relative mx-auto">
                    <div
                        className={cn(
                            getConnectorClasses(),
                            "absolute top-0 z-10 h-full",
                        )}
                    ></div>

                    {/* === MODIFICATION START === */}
                    {/* Enhanced Progress Indicator with Traveling Glow */}
                    {progressIndicator && (
                        <>
                            {/* The main filled progress line */}
                            <motion.div
                                className="absolute top-0 z-10"
                                style={{
                                    height: progressHeight,
                                    width: progressLineWidth,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    borderRadius:
                                        progressLineCap === "round"
                                            ? "9999px"
                                            : "0px",
                                    background: `linear-gradient(to bottom, var(--primary-glow-1), var(--primary), var(--primary-glow-2))`,
                                    // Enhanced shadow for a constant glow effect along the path
                                    boxShadow: `
                                        0 0 15px oklch(from var(--primary-glow-2) l c h / 0.5),
                                        0 0 25px oklch(from var(--primary-glow-3) l c h / 0.3)
                                    `,
                                }}
                            />
                            {/* The traveling glow "comet" at the head of the line */}
                            <motion.div
                                className="absolute z-20"
                                style={{
                                    top: progressHeight,
                                    left: "50%",
                                    translateX: "-50%",
                                    translateY: "-50%", // Center the comet on the line's end point
                                }}
                            >
                                <motion.div
                                    className="h-5 w-5 rounded-full" // Size of the comet core
                                    style={{
                                        background:
                                            "radial-gradient(circle, oklch(from var(--primary-glow-3) l c h / 0.8) 0%, oklch(from var(--primary-glow-2) l c h / 0.5) 40%, oklch(from var(--primary-glow-1) l c h / 0) 70%)",
                                        // Intense, layered glow effect for the comet
                                        boxShadow: `
                                            0 0 15px 4px oklch(from var(--primary-glow-3) l c h / 0.6),
                                            0 0 25px 8px oklch(from var(--primary-glow-2) l c h / 0.4),
                                            0 0 40px 15px oklch(from var(--primary-glow-1) l c h / 0.2)
                                        `,
                                    }}
                                    animate={{
                                        scale: [1, 1.3, 1],
                                    }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                    }}
                                />
                            </motion.div>
                        </>
                    )}
                    {/* === MODIFICATION END === */}

                    <div className="relative z-20">
                        {events.map((event, index) => (
                            <TimelineItem
                                key={event.id || index}
                                event={event}
                                index={index}
                                activeIndex={activeIndex}
                                containerClassName={cn(
                                    "relative mb-20 flex items-center py-4",
                                    "flex-col lg:flex-row",
                                    cardAlignment === "alternating"
                                        ? index % 2 === 0
                                            ? "lg:justify-start"
                                            : "lg:flex-row-reverse lg:justify-start"
                                        : cardAlignment === "left"
                                          ? "lg:justify-start"
                                          : "lg:flex-row-reverse lg:justify-start",
                                )}
                                cardClassName={cn(
                                    getCardClasses(index),
                                    "mt-12 lg:mt-0",
                                )}
                                cardVariants={getCardVariants(index)}
                                parallaxIntensity={parallaxIntensity}
                                smoothProgress={smoothProgress}
                                registerRef={(el) => {
                                    timelineRefs.current[index] = el
                                }}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
